import { SOCKET_CONFIG } from "@/config/socketConf";
import { io, Socket } from "socket.io-client";
import { SocketEvents } from "./socketEvents";

type EventCallback = (data: any) => void;

export class SocketService {
  private socket: Socket | null = null;
  // 🗂️ Registry to hold React's callbacks safely
  private subscribers: Map<string, Set<EventCallback>> = new Map();
  // 📌 Client→server messages that must be REPLAYED on every (re)connect.
  // Server-side room membership (e.g. the recent-activity fan-out room) lives on
  // the socket, so a dropped connection silently un-joins you. Without the
  // replay a brief network blip would kill a live feed until a full remount.
  // Keyed by `key` (defaults to the event name) so the SAME event can be
  // sticky for several payloads at once — two open chats on two leads each
  // hold their own `internal:chat:watch { leadId }`.
  private stickyEmits: Map<string, { event: string; payload: any }> =
    new Map();

  connect(token: string) {
    if (this.socket) {
      console.log("[Socket] ♻️ Reusing existing socket instance.");
      this.socket.auth = { token };

      // ⚠️ CRITICAL: Force reconnect to update Auth Token on Server
      if (this.socket.connected) {
        this.socket.disconnect();
      }
      this.socket.connect();
      return;
    }

    console.log("[Socket] 🆕 Creating new socket instance.");
    this.socket = io(SOCKET_CONFIG.URL, {
      ...SOCKET_CONFIG.OPTIONS,
      auth: { token },
    });

    this.socket.on("connect", () => {
      console.log("[Socket] 🟢 Connected (ID: " + this.socket?.id + ")");
      // Re-join any rooms this client had opted into before the drop.
      this.stickyEmits.forEach(({ event, payload }) => {
        this.socket?.emit(event, payload);
      });
    });

    // ⚡ THE CENTRAL DISPATCHER
    // Instead of multiple listeners, we listen once and distribute.
    this.socket.onAny((eventName, ...args) => {
      console.log(`[Socket 📨] Dispatching: '${eventName}'`);
      const callbacks = this.subscribers.get(eventName);
      if (callbacks) {
        callbacks.forEach((cb) => cb(args[0])); // Call React's functions
      }
    });

    this.socket.connect();
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      console.log("[Socket] ⚪ Disconnected.");
    }
  }

  /** Fire-and-forget client→server message. No-op while disconnected. */
  emit(event: string, payload?: any) {
    this.socket?.emit(event, payload);
  }

  /**
   * Emit now AND on every future reconnect — for messages that establish
   * server-side state (room joins) rather than one-off signals. `key`
   * defaults to the event name; pass one when the same event is held for
   * several payloads (e.g. `internal:chat:watch:<leadId>`).
   */
  addStickyEmit(event: string, payload?: any, key: string = event) {
    this.stickyEmits.set(key, { event, payload });
    this.socket?.emit(event, payload);
  }

  /**
   * Stop replaying the sticky emit under `key` (the event name unless one was
   * given), and send its paired teardown message if given.
   */
  removeStickyEmit(key: string, teardownEvent?: string, payload?: any) {
    this.stickyEmits.delete(key);
    if (teardownEvent) this.socket?.emit(teardownEvent, payload);
  }

  // 👇 React calls this to subscribe
  subscribe(event: SocketEvents, callback: EventCallback) {
    if (!this.subscribers.has(event)) {
      this.subscribers.set(event, new Set());
    }
    this.subscribers.get(event)?.add(callback);

    // Return a cleanup function for useEffect
    return () => {
      this.subscribers.get(event)?.delete(callback);
    };
  }
}

export const socketService = new SocketService();
